import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { getURLParams } from '@lib/getUrlParams';
import { useAuth0Token } from '../../hooks/useAuth0Token';
import useSelector from '../../redux/typedHooks';
import { actions } from '../../redux/modules/user';
import { actions as membershipActions } from '../../redux/modules/myMembership';
import { actions as menuActions } from '../../redux/modules/globalSettings';
import { actions as internalSearchActions } from '../../redux/modules/internalSiteSearch';
import { useKeyboardEvent } from '../../hooks/useKeyboardEvent';
import useOutsideAlerter from '../../hooks/useOutsideAlerter';
import { disableScroll, enableScroll } from '../../hooks/useScrollBlock';
import { updateUrlQueryValues } from '../../utils/updateUrlQueryValues';
import { Decorations } from './Decorations/Decorations';
import MegaMenuOverlay from '../MegaMenuOverlay/MegaMenuOverlay';
import MobileMenuOverlay from '../MobileMenuOverlay/MobileMenuOverlay';
import DesktopNavbar from './DesktopNavbar/DesktopNavbar';
import MobileNavbar from './MobileNavbar/MobileNavbar';
import { SearchModal } from '../SearchModal/SearchModal';
import * as S from './Navbar.styles';

const Navbar = () => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [navOffset, setNavOffset] = useState(0);

  const navBarRef = useRef<HTMLDivElement>(null);
  const navBarWrapperRef = useRef<HTMLDivElement>(null);

  const { simpleToken } = useAuth0Token();
  const { isAuthenticated, isLoading } = useAuth0();
  const dispatch = useDispatch();

  const searchInputText = useSelector((state) => state.internalSiteSearch.query);
  // One value per selector: selecting the whole `user` slice re-rendered the navbar on any change to it.
  const isMenuOpen = useSelector((state) => state.user.isMenuOpen);
  const isSearchOverlayOpen = useSelector((state) => state.user.isSearchOverlayOpen);
  const showSystemMessage = useSelector((state) => state.user.showSystemMessage);
  const isSearchActive = useSelector((state) => state.user.isSearchActive);
  const isMyNAFEnabled = useSelector((state) => state.application.toggledFeatures.myNAF);
  const isSearchEnabled = useSelector((state) => state.application.toggledFeatures.search);
  const customer = useSelector((state) => state.myMembership?.customerInformation?.data?.customer);
  const profileMenu = useSelector((state) => state.globalSettings.settings?.profileMenu);
  const navbarItems = useSelector((state) => state.globalSettings.settings?.navigationBar?.items);
  const searchSettings = useSelector((state) => state.globalSettings.settings?.search);
  const accountName =
    customer?.firstName && typeof customer.firstName === 'string' ? customer.firstName.split(' ')[0] : null;

  const submitSearch = (searchText?: string, isFromPopularTerm = false) => {
    const query = searchText ?? searchInputText;
    if (!query.trim()) return false;
    dispatch(internalSearchActions.getSearchResults.request(isFromPopularTerm));
    updateUrlQueryValues.updateSearchQuery(query);
    if (!isSearchOverlayOpen) dispatch(actions.toggleSearchOverlay(true));
    if (!isSearchActive) dispatch(actions.toggleSearchActive(true));
    return false;
  };

  const onSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitSearch();
    return false;
  };

  const resetSearch = () => {
    dispatch(internalSearchActions.reset());
    if (isSearchActive) dispatch(actions.toggleSearchActive(false));
    updateUrlQueryValues.updateSearchQuery('');
  };

  const onCloseSearch = () => {
    resetSearch();
    dispatch(actions.toggleSearchOverlay(false));
    enableScroll();
  };

  const onCloseSearchResults = () => {
    resetSearch();
  };

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const element = e.target as HTMLInputElement;
    dispatch(internalSearchActions.updateQuery(element.value));
  };

  const onPopularSearchClick = (searchText: string) => {
    dispatch(internalSearchActions.updateQuery(searchText));
    submitSearch(searchText, true);
  };

  const onOpenSearch = () => {
    if (showProfileMenu) setShowProfileMenu(false);
    if (isMenuOpen) {
      dispatch(actions.closeMenu());
      enableScroll();
    }
    dispatch(internalSearchActions.reset());
    dispatch(actions.toggleSearchActive(false));
    dispatch(actions.toggleSearchOverlay(true));
    updateUrlQueryValues.updateSearchQuery('');
    disableScroll();
  };

  const onHamburgerMenuClick = () => {
    if (isSearchOverlayOpen) onCloseSearch();
    if (showProfileMenu) setShowProfileMenu(false);
    dispatch(actions.toggleMenu());
    if (!isMenuOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
  };

  const onProfileMenuClick = () => {
    if (isSearchOverlayOpen) onCloseSearch();
    if (isMenuOpen) {
      dispatch(actions.closeMenu());
      enableScroll();
    }
    setShowProfileMenu((prev) => !prev);
  };

  const onNavClick = () => {
    if (isMenuOpen) {
      dispatch(actions.closeMenu());
    }
    if (isSearchOverlayOpen) {
      onCloseSearch();
    }
    enableScroll();
  };

  useEffect(() => {
    if (navBarRef?.current && showSystemMessage) {
      setNavOffset(navBarRef.current.getBoundingClientRect().top);
    }
  }, [showSystemMessage]);

  useEffect(() => {
    if (isAuthenticated && simpleToken && !customer?.customerId) {
      dispatch(membershipActions.getCustomerInformation.request(simpleToken));
    }
  }, [simpleToken, isAuthenticated, customer?.customerId, dispatch]);

  useEffect(() => {
    if (!searchSettings || !profileMenu || profileMenu.length < 1) {
      dispatch(menuActions.getGlobalSettings.request(''));
    }
  }, [dispatch, profileMenu, navbarItems, searchSettings]);

  useEffect(() => {
    const parsedParams = getURLParams();
    if (typeof parsedParams.search !== 'undefined' && parsedParams.search !== '') {
      dispatch(internalSearchActions.updateQuery(parsedParams.search));
      dispatch(internalSearchActions.getSearchResults.request(false));
      dispatch(actions.toggleSearchActive(true));
      dispatch(actions.toggleSearchOverlay(true));
      disableScroll();
    }
  }, [dispatch]);

  useOutsideAlerter(navBarWrapperRef, isMenuOpen || showProfileMenu, () => {
    if (isMenuOpen) {
      dispatch(actions.closeMenu());
      enableScroll();
    }
    if (showProfileMenu) {
      setShowProfileMenu(false);
    }
    if (isSearchOverlayOpen) {
      onCloseSearch();
    }
  });

  useKeyboardEvent('Escape', () => {
    if (isSearchOverlayOpen) {
      onCloseSearch();
    } else if (isMenuOpen || showProfileMenu) {
      dispatch(actions.closeMenu());
      setShowProfileMenu(false);
      enableScroll();
    }
  });

  return (
    <>
      <S.Overlay $isMenuOpen={isMenuOpen} $isSearchOpen={isSearchOverlayOpen} />
      <S.Wrapper
        data-elastic-exclude
        $isMenuOpen={isMenuOpen}
        ref={navBarWrapperRef}
        $isSearchOpen={isSearchOverlayOpen}
      >
        <S.Container ref={navBarRef} $isMenuOpen={isMenuOpen} $isSearchOpen={isSearchOverlayOpen}>
          <S.DecorationWrapper $isMenuOpen={isMenuOpen}>
            <Decorations />
          </S.DecorationWrapper>
          <DesktopNavbar
            isSearchEnabled={isSearchEnabled}
            onNavClick={onNavClick}
            onHamburgerMenuClick={onHamburgerMenuClick}
            onProfileMenuClick={onProfileMenuClick}
            onSearchClick={onOpenSearch}
            showProfileMenu={showProfileMenu}
            setShowProfileMenu={setShowProfileMenu}
            isAuthenticated={isAuthenticated}
            isMyNAFEnabled={isMyNAFEnabled}
            isLoading={isLoading}
            accountName={accountName}
            isMenuOpen={isMenuOpen}
          />
          <MobileNavbar
            isSearchEnabled={isSearchEnabled}
            onNavClick={onNavClick}
            onHamburgerMenuClick={onHamburgerMenuClick}
            onProfileMenuClick={onProfileMenuClick}
            onSearchClick={onOpenSearch}
            showProfileMenu={showProfileMenu}
            setShowProfileMenu={setShowProfileMenu}
            isAuthenticated={isAuthenticated}
            isMyNAFEnabled={isMyNAFEnabled}
            isLoading={isLoading}
            accountName={accountName}
            isMenuOpen={isMenuOpen}
          />
          <MegaMenuOverlay menuOffset={navOffset} ref={navBarRef} isMenuOpen={isMenuOpen} />
          <MobileMenuOverlay isMenuOpen={isMenuOpen} />
        </S.Container>
      </S.Wrapper>
      {isSearchOverlayOpen && (
        <SearchModal
          isSearchActive={isSearchActive}
          navBarRef={navBarRef}
          onClose={onCloseSearch}
          onCloseResults={onCloseSearchResults}
          onPopularSearchClick={onPopularSearchClick}
          onSearch={onSearch}
          onSearchInputChange={onSearchInputChange}
        />
      )}
    </>
  );
};

// Navbar takes no props, so memo keeps it (and its ~850 components) out of ThemedApp's re-renders,
// several per client-side navigation. It still re-renders for its own store and auth state.
export default memo(Navbar);
